import { useEffect, useState } from 'react';

/**
 * Avatar component that displays profile image or initials fallback
 * @param {string} src - Profile image URL
 * @param {string} name - User's name (for initials)
 * @param {string} className - Additional CSS classes
 * @param {string} alt - Alt text for image
 * @param {number} size - Size of avatar in pixels (default: 40)
 */
function Avatar({ src, name = '', className = '', alt = '', size = 40 }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Reset loading/error state when the src changes (important for updated profile images)
  useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [src]);

  // Get initials from name
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const initials = getInitials(name);
  const displayInitials = initials.length > 2 ? initials.substring(0, 2) : initials;

  // Color palette for avatar backgrounds (based on name hash for consistency)
  const getAvatarColor = (name) => {
    if (!name) return 'bg-gray-500';
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-cyan-500',
    ];
    // Simple hash function to get consistent color for same name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const avatarColor = getAvatarColor(name);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // If image failed to load or no src provided, show initials
  if (imageError || !src || !src.trim()) {
    return (
      <div
        className={`${avatarColor} text-white font-semibold flex items-center justify-center rounded-full ${className}`}
        style={{ width: size, height: size, minWidth: size, fontSize: size * 0.4 }}
        aria-label={alt || `${name}'s avatar`}
      >
        {displayInitials}
      </div>
    );
  }

  // Show image with initials as fallback
  return (
    <div className={`relative rounded-full ${className}`} style={{ width: size, height: size, minWidth: size }}>
      <img
        src={src}
        alt={alt || `${name}'s profile`}
        className={`w-full h-full object-cover rounded-full ${!imageLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
        onError={handleImageError}
        onLoad={handleImageLoad}
      />
      {!imageLoaded && !imageError && (
        <div
          className={`absolute inset-0 ${avatarColor} text-white font-semibold flex items-center justify-center rounded-full`}
          style={{ fontSize: size * 0.4 }}
        >
          {displayInitials}
        </div>
      )}
    </div>
  );
}

export default Avatar;

